
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogUrl?: string;
  noIndex?: boolean;
}

export function SEO({
  title = '라스베가스 한인 커뮤니티 - VegasKFam',
  description = '라스베가스 한인들을 위한 종합 정보 커뮤니티. 구인구직, 부동산, 사고팔고, 비즈니스, 여행 정보를 제공합니다.',
  keywords = '라스베가스 한인, 라스베가스 한인 커뮤니티, 라스베가스 한인타운, 한인 모임, 라스베가스 한인회, 라스베가스 교민, 라스베가스 한인 정보',
  ogImage = '/og-image.png',
  ogUrl,
  noIndex = false,
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;
    
    // Update meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Control robots indexing
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }
    
    // Update Open Graph tags
    updateOpenGraphTag('og:title', title);
    updateOpenGraphTag('og:description', description);
    updateOpenGraphTag('og:image', ogImage);
    updateOpenGraphTag('og:site_name', '라스베가스 한인 커뮤니티 - VegasKFam');
    
    if (ogUrl) {
      updateOpenGraphTag('og:url', ogUrl);
    }
    
    // Add Twitter Card tags
    updateTwitterTag('twitter:card', 'summary_large_image');
    updateTwitterTag('twitter:title', title);
    updateTwitterTag('twitter:description', description);
    updateTwitterTag('twitter:image', ogImage);
    
    // Update language tag
    document.documentElement.lang = 'ko';
    
    // Add structured data for local business (JSON-LD)
    addStructuredData();
    
    return () => {
      // Remove structured data on component unmount if needed
      const existingScript = document.getElementById('structured-data');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [title, description, keywords, ogImage, ogUrl, noIndex]);
  
  return null;
}

function updateMetaTag(name: string, content: string) {
  const tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    const meta = document.createElement('meta');
    meta.name = name;
    meta.content = content;
    document.head.appendChild(meta);
  }
}

function updateOpenGraphTag(property: string, content: string) {
  const tag = document.querySelector(`meta[property="${property}"]`);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    const meta = document.createElement('meta');
    meta.setAttribute('property', property);
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
}

function updateTwitterTag(name: string, content: string) {
  const tag = document.querySelector(`meta[name="${name}"]`);
  if (tag) {
    tag.setAttribute('content', content);
  } else {
    const meta = document.createElement('meta');
    meta.name = name;
    meta.content = content;
    document.head.appendChild(meta);
  }
}

function addStructuredData() {
  // Remove any existing structured data
  const existingScript = document.getElementById('structured-data');
  if (existingScript) {
    existingScript.remove();
  }

  // Create structured data script
  const script = document.createElement('script');
  script.id = 'structured-data';
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': '라스베가스 한인 커뮤니티 - VegasKFam',
    'description': '라스베가스 한인들을 위한 종합 정보 커뮤니티',
    'url': 'https://vegaskfam.com',
    'telephone': '',
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Las Vegas',
      'addressRegion': 'NV',
      'addressCountry': 'US'
    },
    'sameAs': [
      'https://facebook.com/vegaskfam',
      'https://instagram.com/vegaskfam'
    ]
  });

  document.head.appendChild(script);
}
